<div class="create-order-dialog">
  <div class="dialog-content-container" [class.p-4]="hasPanelContent()">

    @if (isSubscription()) {
    <!-- Comments only. The two-orders explanation and the cost blocks moved to the summary at the
         bottom of the dialog, so this branch renders nothing at all when there are no comments —
         hence the guard on the wrapper too, which would otherwise contribute bare padding. -->
    @if (hasComments()) {
    <div class="flex flex-column gap-4 pt-2">
      <div class="flex align-items-center gap-3">
        <p-toggleswitch [ngModel]="commentsOK()" (ngModelChange)="onCommentsOKChange($event)"
          [disabled]="anyCommentSelected()">
        </p-toggleswitch>
        <label>{{ 'REVIEW_OR_CREATE_ORDER.COMMENTS_ARE_INCLUDED_CHECK' | translate }}</label>
      </div>

      <app-create-order-dialog-comments [comments]="comments()"
        (setCommentsToInclude)="onCommentsToIncludeChange($event)">
      </app-create-order-dialog-comments>
    </div>
    }

    <!-- Embedded, the stepper renders this at the very bottom of the dialog instead, below the
         destination choice. Outside the comments guard: a subscription quote with no comments
         still has two orders to show. -->
    @if (!embedded()) {
    <div class="p-4">
      <app-create-order-summary />
    </div>
    }

    <!-- Actions. Suppressed when embedded: the stepper footer drives the flow instead. -->
    @if (!embedded()) {
    <div class="flex justify-content-end gap-2 mt-5">
      <p-button [label]="'ABORT' | translate" [text]="true" (onClick)="onCancel()">
      </p-button>
      <p-button [label]="'CREATE.TITLE' | translate" [disabled]="!canConfirm()" (onClick)="onConfirm()">
      </p-button>
    </div>
    }
    } @else {

    @if (hasPanelContent()) {
    <p-tabs [value]="activeTab()">
      <!-- Only when there is somewhere else to go. A lone "Bekräftelse" tab is a label for the
           panel you are already looking at, and it was the whole visible content of this step
           whenever the quote had nothing left to confirm. -->
      @if (hasComments()) {
      <p-tablist>
        <p-tab value="0">{{ 'CREATE_ORDER.TABS.CONFIRMATION' | translate }}</p-tab>
        <p-tab value="1">
          {{ 'CREATE_ORDER.TABS.COMMENTS' | translate }}
          @if (selectedCommentsCount() > 0) {
          ({{ selectedCommentsCount() }} {{ 'CREATE_ORDER.TABS.COMMENTS_SELECTED' | translate }})
          }
        </p-tab>
      </p-tablist>
      }
      <p-tabpanels>
        <!-- Tab 1: Confirmation -->
        <p-tabpanel value="0">
          <div class="flex flex-column gap-4 pt-4">

            @if (isUnderReview()) {
            <!-- Send mail toggle -->
            <div class="flex align-items-center gap-3">
              <p-toggleswitch [ngModel]="sendMail()" (ngModelChange)="onSendMailChange($event)">
              </p-toggleswitch>
              <label>{{ 'REVIEW_ORDER.SHOULD_SEND_MAIL' | translate }}</label>
            </div>

            <!-- Email form (if sendMail is true) -->
            @if (sendMail()) {
            <ng-container [formGroup]="emailForm">
              <!-- Recipient Email -->
              <div>
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-envelope"></i></p-inputgroup-addon>
                  <p-floatlabel variant="on" class="flex-1">
                    <input id="recipientEmail" pInputText formControlName="recipientEmail" class="w-full" />
                    <label for="recipientEmail">{{ "QUOTE.DETAIL.QUOTE_INFORMATION.SEND_TO" | translate }} *</label>
                  </p-floatlabel>
                </p-inputgroup>
                <app-core-errors [control]="emailForm.get('recipientEmail')"></app-core-errors>
              </div>

              <!-- CC Emails -->
              <div>
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-users"></i></p-inputgroup-addon>
                  <p-floatlabel variant="on" class="flex-1">
                    <input id="ccEmail" pInputText [formControl]="ccEmailControl"
                      (keydown.enter)="addCcRecipient(); $event.preventDefault()" (blur)="addCcRecipient()"
                      class="w-full" />
                    <label for="ccEmail">{{ "QUOTE.DETAIL.QUOTE_INFORMATION.SEND_COPY_TO" | translate }}</label>
                  </p-floatlabel>
                </p-inputgroup>
                <app-core-errors [control]="ccEmailControl"></app-core-errors>
                @if (validCcEmails.length > 0) {
                <div class="flex flex-wrap gap-2 mt-2">
                  @for (email of validCcEmails; track email) {
                  <p-chip [label]="email" [removable]="true" (onRemove)="removeCcRecipient(email)" />
                  }
                </div>
                }
              </div>

              <!-- Message -->
              <div>
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-envelope"></i></p-inputgroup-addon>
                  <p-floatlabel variant="on" class="flex-1">
                    <textarea id="message" pTextarea formControlName="message" [rows]="4" [autoResize]="true"
                      class="w-full"></textarea>
                    <label for="message">{{ "SEND_QUOTE.LBL_MESSAGE" | translate }}</label>
                  </p-floatlabel>
                </p-inputgroup>
              </div>

              <!-- Add Comment Toggle -->
              <div class="flex align-items-center gap-3">
                <p-toggleswitch [ngModel]="showCommentField()" (ngModelChange)="onShowCommentFieldChange($event)"
                  [ngModelOptions]="{ standalone: true }" inputId="showCommentField" />
                <label for="showCommentField">{{ "SEND_QUOTE.ADD_COMMENT" | translate }}</label>
              </div>

              <!-- Comment -->
              @if (showCommentField()) {
              <div>
                <p-inputgroup>
                  <p-inputgroup-addon><i class="pi pi-comment"></i></p-inputgroup-addon>
                  <p-floatlabel variant="on" class="flex-1">
                    <textarea id="comment" pTextarea formControlName="comment" [rows]="4" [autoResize]="true"
                      class="w-full"></textarea>
                    <label for="comment">{{ "SEND_QUOTE.LBL_COMMENT" | translate }}</label>
                  </p-floatlabel>
                </p-inputgroup>
              </div>
              }
            </ng-container>
            }
            <p-divider> </p-divider>

            }

            <!-- Comments confirmation toggle (disabled when comments are selected) -->
            @if (hasComments()) {
            <div class="flex align-items-center gap-3">
              <p-toggleswitch [ngModel]="commentsOK()" (ngModelChange)="onCommentsOKChange($event)"
                [disabled]="anyCommentSelected()">
              </p-toggleswitch>
              <label>{{ 'REVIEW_OR_CREATE_ORDER.COMMENTS_ARE_INCLUDED_CHECK' | translate }}</label>
            </div>
            }

            <!-- Manual credit-check attestation. Only where the real flow is unavailable;
                 elsewhere the step dialog before this one carries it. -->
            @if (showCreditCheck()) {
            <div class="flex align-items-center gap-3">
              <p-toggleswitch [ngModel]="creditCheckOK()" (ngModelChange)="onCreditCheckChange($event)">
              </p-toggleswitch>
              <label>{{ 'REVIEW_OR_CREATE_ORDER.CREDIT_CHECK_CERTIFY' | translate }}</label>
            </div>
            }

            <!-- Credit-check result, read-only. This is what the user is confirming, so it belongs
                 on the confirmation tab even though the step dialog just showed it. -->
            @if (creditCheck(); as cc) {
            <div>
              <div class="font-medium mb-2">{{ 'QUOTE.CREDIT_CHECK.DIALOG_TITLE' | translate }}</div>
              <app-quote-credit-check-status
                [check]="cc"
                [currentTotal]="quoteTotalIncVat()" />
            </div>
            }

          </div>
        </p-tabpanel>

        <!-- Tab 2: Comments (only when comments exist) -->
        @if (hasComments()) {
        <p-tabpanel value="1">
          <div class="pt-4">
            <app-create-order-dialog-comments [comments]="comments()"
              (setCommentsToInclude)="onCommentsToIncludeChange($event)">
            </app-create-order-dialog-comments>
          </div>
        </p-tabpanel>
        }
      </p-tabpanels>
    </p-tabs>
    }

    <!-- Embedded, the stepper renders this at the very bottom of the dialog instead. -->
    @if (!embedded()) {
    <div class="mt-4">
      <app-create-order-summary />
    </div>
    }

    <!-- Actions. Suppressed when embedded: the stepper footer drives the flow instead. -->
    @if (!embedded()) {
    <div class="flex justify-content-end gap-2 mt-5">
      <p-button [label]="'ABORT' | translate" [text]="true" (onClick)="onCancel()">
      </p-button>
      <p-button [label]="'CREATE.TITLE' | translate" [disabled]="!canConfirm()" (onClick)="onConfirm()">
      </p-button>
    </div>
    }
    }
  </div>
</div>